/**
 * Keyboard navigation inside a `Pop` panel.
 *
 * Split out of `Pop.tsx` because none of it is React: it is DOM reading and
 * index arithmetic, and keeping it here leaves the component about mounting,
 * positioning and closing. `nextIndex` is the one piece that needs no DOM at
 * all, which is why it is exported on its own - see `popFocus.test.ts`.
 */

/**
 * What a keyboard can land on. `:not(:disabled)` is load-bearing: the template
 * menu disables every row while one template loads, and arrowing onto a
 * disabled row would look like the menu had stopped responding.
 */
export const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

const GROUP = '[role="group"]';

/** Wrap-around step. Coming from nowhere (`at < 0`) enters at whichever end the
 *  direction implies, so ArrowUp on a fresh panel lands on the last row. */
export function nextIndex(count: number, at: number, delta: number): number {
  if (count <= 0) return -1;
  if (at < 0) return delta > 0 ? 0 : count - 1;
  return (((at + delta) % count) + count) % count;
}

/**
 * The rows ArrowUp and ArrowDown walk between.
 *
 * A `role="group"` counts as ONE stop, not as one per member. The account menu
 * embeds two segmented switches (theme, language) whose buttons are arranged
 * side by side; treated as rows they would turn into five phantom entries and
 * ArrowDown would walk sideways through the theme switch. Left and Right move
 * inside the group instead, which is what a segmented control expects anyway.
 */
export function focusableStops(panel: HTMLElement): HTMLElement[] {
  const seen = new Set<Element>();
  const out: HTMLElement[] = [];
  for (const el of panel.querySelectorAll<HTMLElement>(FOCUSABLE)) {
    const group = el.closest(GROUP);
    if (group && group !== panel) {
      if (seen.has(group)) continue;
      seen.add(group);
      // Enter a switch on the segment that is currently on, not on its first:
      // landing on "light" while the theme is dark reads as having changed it.
      out.push(group.querySelector<HTMLElement>('[aria-pressed="true"]') ?? el);
      continue;
    }
    out.push(el);
  }
  return out;
}

/**
 * One ArrowUp/ArrowDown step inside a panel.
 *
 * The three lines both menu kinds need: read the stops, work out where focus is
 * now, move it. `from` overrides the second of those, which is how Home and End
 * are expressed - they are "step forward from nowhere" and "step back from
 * nowhere" rather than key handling of their own.
 */
export function stepFocus(panel: HTMLElement | null, delta: number, from?: number): void {
  if (!panel) return;
  const stops = focusableStops(panel);
  if (stops.length === 0) return;
  const at = from ?? stopIndex(stops, document.activeElement);
  stops[nextIndex(stops.length, at, delta)]?.focus();
}

/** Where focus currently sits among the stops, counting anywhere inside a group
 *  as that group's stop. -1 when focus is on the panel itself or outside it. */
export function stopIndex(stops: HTMLElement[], active: Element | null): number {
  if (!active) return -1;
  const activeGroup = active.closest(GROUP);
  return stops.findIndex((el) => el === active || (activeGroup !== null && el.closest(GROUP) === activeGroup));
}

/**
 * Left/Right inside a segmented switch. Returns false when focus is not in one,
 * so the caller can leave the arrow key alone rather than swallow it.
 */
export function moveWithinGroup(active: Element | null, delta: number): boolean {
  const group = active?.closest(GROUP);
  if (!group) return false;
  const members = [...group.querySelectorAll<HTMLElement>(FOCUSABLE)];
  const at = members.indexOf(active as HTMLElement);
  if (at < 0) return false;
  members[nextIndex(members.length, at, delta)]?.focus();
  return true;
}
